import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import PublicidadModal from "../components/PublicidadModal";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  LayoutDashboard,
  Loader2,
  RefreshCw,
  Package,
  ClipboardList,
  Users,
  Ticket,
  MessageSquare,
  Truck,
  UserCog,
  FileCheck,
  TrendingUp,
  AlertTriangle,
  Bell,
  DollarSign,
  Euro,
  ArrowRight,
  Clock,
  CheckCircle,
  XCircle,
  Hourglass,
  CalendarDays,
  UserCheck,
  Shield,
  Activity,
  BarChart3,
  Settings2,
  GripVertical,
  Eye,
  EyeOff,
  RotateCcw,
  Globe,
  ExternalLink,
  Save,
  X,
  ChevronUp,
  ChevronDown,
  Plus,
  Trash2,
  Target,
  Zap,
  Star
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend } from 'recharts';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const MODULE_CONFIG = {
  tickets: { 
    label: "Mesa de Ayuda", 
    description: "Tickets y solicitudes de soporte",
    icon: Ticket, 
    color: "from-orange-500 to-amber-500",
    bgColor: "bg-orange-50 dark:bg-orange-900/20",
    textColor: "text-orange-700 dark:text-orange-400",
    path: "/mesa-ayuda/tickets"
  },
  mensajeria: { 
    label: "Mensajería", 
    description: "Envíos y entregas",
    icon: Truck, 
    color: "from-indigo-500 to-violet-500",
    bgColor: "bg-indigo-50 dark:bg-indigo-900/20",
    textColor: "text-indigo-700 dark:text-indigo-400",
    path: "/mensajeria/solicitudes"
  },
  gestion_humana: { 
    label: "Gestión Humana", 
    description: "Vacaciones, permisos y solicitudes",
    icon: UserCog, 
    color: "from-rose-500 to-pink-500",
    bgColor: "bg-rose-50 dark:bg-rose-900/20",
    textColor: "text-rose-700 dark:text-rose-400",
    path: "/gestion-humana"
  },
  requisiciones: { 
    label: "Requisiciones", 
    description: "Solicitudes de materiales",
    icon: ClipboardList, 
    color: "from-purple-500 to-pink-500",
    bgColor: "bg-purple-50 dark:bg-purple-900/20",
    textColor: "text-purple-700 dark:text-purple-400",
    path: "/requisiciones"
  },
  inventario: { 
    label: "Inventario", 
    description: "Stock y artículos",
    icon: Package, 
    color: "from-blue-500 to-cyan-500",
    bgColor: "bg-blue-50 dark:bg-blue-900/20",
    textColor: "text-blue-700 dark:text-blue-400",
    path: "/inventario"
  },
  visitantes: { 
    label: "Visitantes", 
    description: "Control de acceso",
    icon: Users, 
    color: "from-green-500 to-emerald-500",
    bgColor: "bg-green-50 dark:bg-green-900/20",
    textColor: "text-green-700 dark:text-green-400",
    path: "/visitantes"
  },
  whatsapp: { 
    label: "WhatsApp", 
    description: "Mensajes y conversaciones",
    icon: MessageSquare, 
    color: "from-emerald-500 to-teal-500",
    bgColor: "bg-emerald-50 dark:bg-emerald-900/20",
    textColor: "text-emerald-700 dark:text-emerald-400",
    path: "/whatsapp/bandeja"
  },
  comparaciones: { 
    label: "Pólizas", 
    description: "Comparación de pólizas",
    icon: FileCheck, 
    color: "from-slate-500 to-gray-600",
    bgColor: "bg-slate-50 dark:bg-slate-700/50",
    textColor: "text-slate-700 dark:text-slate-300",
    path: "/comparar"
  }
};

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

const STATUS_CONFIG = {
  pendiente: { label: "Pendiente", color: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400", icon: Hourglass },
  abierto: { label: "Abierto", color: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400", icon: Clock },
  en_progreso: { label: "En Progreso", color: "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400", icon: Activity },
  en_transito: { label: "En Tránsito", color: "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400", icon: Truck },
  completado: { label: "Completado", color: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400", icon: CheckCircle },
  resuelto: { label: "Resuelto", color: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400", icon: CheckCircle },
  aprobada: { label: "Aprobada", color: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400", icon: CheckCircle },
  rechazada: { label: "Rechazada", color: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400", icon: XCircle },
  cancelado: { label: "Cancelado", color: "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300", icon: XCircle }
};

const formatCurrency = (value) => {
  if (typeof value !== 'number') return 'RD$0.00';
  return `RD$${value.toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('es-DO', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Sortable Widget Item Component
function SortableWidgetItem({ id, children, isConfigMode }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id, disabled: !isConfigMode });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1
  };

  return (
    <div ref={setNodeRef} style={style} className="relative">
      {isConfigMode && (
        <div
          {...attributes}
          {...listeners}
          className="absolute -left-2 top-1/2 -translate-y-1/2 z-10 p-1.5 bg-slate-200 dark:bg-slate-600 rounded-lg cursor-grab active:cursor-grabbing hover:bg-slate-300 dark:hover:bg-slate-500 transition-colors"
        >
          <GripVertical className="w-4 h-4 text-slate-600 dark:text-slate-300" />
        </div>
      )}
      {children}
    </div>
  );
}

export default function MyDashboardPage() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({});
  const [lastUpdate, setLastUpdate] = useState(null);
  const [configMode, setConfigMode] = useState(false);
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [dashboardConfig, setDashboardConfig] = useState({
    widgets: Object.keys(MODULE_CONFIG).map((key, index) => ({
      widget_id: key,
      enabled: true,
      order: index,
      size: "normal"
    })),
    show_charts: true,
    show_activity: true,
    show_quick_stats: true,
    layout: "grid"
  });
  
  // Custom widgets state
  const [customWidgets, setCustomWidgets] = useState([]);
  const [customWidgetsData, setCustomWidgetsData] = useState({});
  const [availableFilters, setAvailableFilters] = useState(null);
  const [showCustomWidgetDialog, setShowCustomWidgetDialog] = useState(false);
  const [newCustomWidget, setNewCustomWidget] = useState({
    name: "",
    module: "",
    metric: "count",
    filters: {},
    color: "blue",
    icon: "BarChart3"
  });
  const [creatingWidget, setCreatingWidget] = useState(false);
  
  // Supervisados state
  const [supervisados, setSupervisados] = useState([]);
  const [isSupervisor, setIsSupervisor] = useState(false);
  
  // Tasas de cambio
  const [tasas, setTasas] = useState(null);
  const [loadingTasas, setLoadingTasas] = useState(false);
  
  // Filtro de mes/año para el Resumen del Mes
  const [mesSeleccionado, setMesSeleccionado] = useState(new Date().getMonth() + 1);
  const [anioSeleccionado, setAnioSeleccionado] = useState(new Date().getFullYear());
  const [periodoActual, setPeriodoActual] = useState(null);
  
  // Accesos a Portales
  const [accesosPortales, setAccesosPortales] = useState([]);
  
  const { getAuthHeaders, user } = useAuth();
  const navigate = useNavigate();

  // Map widget IDs to permission IDs (some differ from widget naming)
  const WIDGET_TO_PERMISSION_MAP = {
    tickets: 'mesa_ayuda',
    mensajeria: 'mensajeria',
    gestion_humana: 'gestion_humana',
    requisiciones: 'requisiciones',
    inventario: 'finanzas_contabilidad',
    visitantes: 'control_visitantes',
    whatsapp: 'whatsapp',
    comparaciones: 'riesgos_generales'
  };

  // Check if user has access to a specific module
  const hasModuleAccess = useCallback((moduleId) => {
    // Admin always has full access
    const userRole = user?.role?.toLowerCase();
    if (userRole === 'admin' || userRole === 'administrador') {
      return true;
    }
    
    if (!user?.menu_permissions) {
      return false;
    }
    
    // Get the permission ID for this widget
    const permissionId = WIDGET_TO_PERMISSION_MAP[moduleId] || moduleId;
    
    // Check primary permission
    const permission = user.menu_permissions[permissionId];
    if (permission?.access === true) {
      return true;
    }
    
    // Also check the original moduleId in case they match
    const directPermission = user.menu_permissions[moduleId];
    if (directPermission?.access === true) {
      return true;
    }
    
    // Default to false (no access)
    return false;
  }, [user]);

  // Get the best accessible path for a module based on user permissions
  const getAccessiblePath = useCallback((moduleId) => {
    const config = MODULE_CONFIG[moduleId];
    if (!config) return null;
    
    const userRole = user?.role?.toLowerCase();
    const isAdmin = userRole === 'admin' || userRole === 'administrador';
    
    // If admin, use default path
    if (isAdmin) {
      return config.path;
    }
    
    const permissions = user?.menu_permissions || {};
    
    // Define fallback paths for each module based on common sub-pages
    const moduleFallbacks = {
      gestion_humana: [
        { permission: 'solicitudes_unificadas', path: '/gestion-humana/solicitud' },
        { permission: 'movimientos_gh', path: '/gestion-humana/movimientos' },
        { permission: 'vacaciones', path: '/gestion-humana/vacaciones' },
        { permission: 'empleados', path: '/gestion-humana/empleados' },
        { permission: 'plantillas_gh', path: '/gestion-humana/plantillas' },
        { permission: 'reclutamiento', path: '/gestion-humana/reclutamiento' },
        { permission: 'parametros_gh', path: '/gestion-humana/parametros' }
      ],
      mesa_ayuda: [
        { permission: 'mis_tickets', path: '/mesa-ayuda/mis-tickets' },
        { permission: 'tickets', path: '/mesa-ayuda/tickets' },
        { permission: 'categorias_ticket', path: '/mesa-ayuda/categorias' },
        { permission: 'prioridades_ticket', path: '/mesa-ayuda/prioridades' }
      ],
      tickets: [
        { permission: 'mis_tickets', path: '/mesa-ayuda/mis-tickets' },
        { permission: 'tickets', path: '/mesa-ayuda/tickets' },
        { permission: 'categorias_ticket', path: '/mesa-ayuda/categorias' },
        { permission: 'prioridades_ticket', path: '/mesa-ayuda/prioridades' }
      ],
      mensajeria: [
        { permission: 'solicitudes', path: '/mensajeria/solicitudes' },
        { permission: 'mensajeros', path: '/mensajeria/mensajeros' },
        { permission: 'historial_mensajeria', path: '/mensajeria/historial' },
        { permission: 'alertas_mensajeria', path: '/mensajeria/alertas' }
      ],
      requisiciones: [
        { permission: 'requisiciones_lista', path: '/requisiciones' },
        { permission: 'requisiciones_nueva', path: '/requisiciones/nueva' },
        { permission: 'requisiciones_pendientes', path: '/requisiciones/pendientes' },
        { permission: 'requisiciones_historial', path: '/requisiciones/historial' }
      ],
      inventario: [
        { permission: 'inventario_dashboard', path: '/inventario' },
        { permission: 'inventario_articulos', path: '/inventario/articulos' },
        { permission: 'inventario_entradas', path: '/inventario/entradas' },
        { permission: 'inventario_movimientos', path: '/inventario/movimientos' }
      ],
      visitantes: [
        { permission: 'visitantes_registrar', path: '/visitantes' },
        { permission: 'visitantes_historial', path: '/visitantes/historial' },
        { permission: 'visitantes_reportes', path: '/visitantes/reportes' }
      ],
      whatsapp: [
        { permission: 'whatsapp_bandeja', path: '/whatsapp/bandeja' },
        { permission: 'whatsapp_conversaciones', path: '/whatsapp/conversaciones' },
        { permission: 'whatsapp_config', path: '/whatsapp/configuracion' }
      ],
      comparaciones: [
        { permission: 'compare', path: '/comparar' },
        { permission: 'coberturas', path: '/coberturas-polizas' },
        { permission: 'reports', path: '/reportes' }
      ]
    };
    
    const fallbacks = moduleFallbacks[moduleId];
    if (fallbacks) {
      // Find the first sub-page the user has access to
      for (const fallback of fallbacks) {
        if (permissions[fallback.permission]?.access === true) {
          return fallback.path;
        }
      }
    }
    
    // If no specific fallback found, return the default path (might be blocked by routing)
    return config.path;
  }, [user]);

  // Color mapping for custom widgets
  const COLOR_MAP = {
    blue: "from-blue-500 to-cyan-500",
    green: "from-green-500 to-emerald-500",
    purple: "from-purple-500 to-pink-500",
    orange: "from-orange-500 to-amber-500",
    red: "from-red-500 to-rose-500",
    indigo: "from-indigo-500 to-violet-500",
    teal: "from-teal-500 to-cyan-500"
  };
  
  // Icon mapping for custom widgets
  const ICON_MAP = {
    BarChart3: BarChart3,
    TrendingUp: TrendingUp,
    Target: Target,
    Zap: Zap,
    Star: Star,
    AlertTriangle: AlertTriangle,
    CheckCircle: CheckCircle
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const fetchDashboardConfig = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/dashboard/config`, getAuthHeaders());
      setDashboardConfig(response.data);
    } catch (err) {
      console.error("Error fetching dashboard config:", err);
    }
  }, [getAuthHeaders]);

  const fetchMyKPIs = useCallback(async (mes = null, anio = null) => {
    try {
      setLoading(true);
      // Construir URL con parámetros de mes y año
      let url = `${API}/dashboard/my-kpis`;
      const params = new URLSearchParams();
      if (mes) params.append('mes', mes);
      if (anio) params.append('anio', anio);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url, getAuthHeaders());
      setData(response.data);
      if (response.data.periodo) {
        setPeriodoActual(response.data.periodo);
      }
      setLastUpdate(new Date());
    } catch (err) {
      console.error("Error fetching KPIs:", err);
      toast.error("Error al cargar el dashboard");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  // Fetch supervisados
  const fetchSupervisados = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/users/mis-supervisados`, getAuthHeaders());
      setSupervisados(response.data.supervisados || []);
      setIsSupervisor(response.data.is_supervisor || false);
    } catch (err) {
      console.error("Error fetching supervisados:", err);
    }
  }, [getAuthHeaders]);

  // Custom widgets functions
  const fetchCustomWidgets = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/dashboard/custom-widgets`, getAuthHeaders());
      setCustomWidgets(response.data);
      // Fetch data for each widget
      response.data.forEach(widget => fetchCustomWidgetData(widget.id));
    } catch (err) {
      console.error("Error fetching custom widgets:", err);
    }
  }, [getAuthHeaders]);

  const fetchCustomWidgetData = async (widgetId) => {
    try {
      const response = await axios.get(`${API}/dashboard/custom-widgets/${widgetId}/data`, getAuthHeaders());
      setCustomWidgetsData(prev => ({ ...prev, [widgetId]: response.data }));
    } catch (err) {
      console.error(`Error fetching widget ${widgetId} data:`, err);
    }
  };

  const fetchAvailableFilters = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/custom-widgets/available-filters`, getAuthHeaders());
      setAvailableFilters(response.data);
    } catch (err) {
      console.error("Error fetching available filters:", err);
    }
  };

  const createCustomWidget = async () => {
    if (!newCustomWidget.name || !newCustomWidget.module) {
      toast.error("Nombre y módulo son requeridos");
      return;
    }
    
    try {
      setCreatingWidget(true);
      await axios.post(`${API}/dashboard/custom-widgets`, newCustomWidget, getAuthHeaders());
      toast.success("Widget personalizado creado");
      setShowCustomWidgetDialog(false);
      setNewCustomWidget({
        name: "",
        module: "",
        metric: "count",
        filters: {},
        color: "blue",
        icon: "BarChart3"
      });
      fetchCustomWidgets();
    } catch (err) {
      console.error("Error creating custom widget:", err);
      toast.error("Error al crear el widget");
    } finally {
      setCreatingWidget(false);
    }
  };

  const deleteCustomWidget = async (widgetId) => {
    try {
      await axios.delete(`${API}/dashboard/custom-widgets/${widgetId}`, getAuthHeaders());
      toast.success("Widget eliminado");
      fetchCustomWidgets();
    } catch (err) {
      console.error("Error deleting widget:", err);
      toast.error("Error al eliminar el widget");
    }
  };

  // Función para cargar tasas de cambio
  const fetchTasas = async () => {
    try {
      setLoadingTasas(true);
      const response = await axios.get(`${API}/tasas`, getAuthHeaders());
      setTasas(response.data);
    } catch (err) {
      console.error("Error al cargar tasas:", err);
    } finally {
      setLoadingTasas(false);
    }
  };

  // Función para cargar accesos a portales
  const fetchAccesosPortales = async () => {
    try {
      const response = await axios.get(`${API}/accesos-portales/activos`, getAuthHeaders());
      setAccesosPortales(response.data);
    } catch (err) {
      console.error("Error al cargar accesos a portales:", err);
    }
  };

  useEffect(() => {
    fetchDashboardConfig();
    fetchMyKPIs(mesSeleccionado, anioSeleccionado);
    fetchCustomWidgets();
    fetchSupervisados();
    fetchTasas();
    fetchAccesosPortales();
    const interval = setInterval(() => fetchMyKPIs(mesSeleccionado, anioSeleccionado), 300000);
    return () => clearInterval(interval);
  }, [fetchMyKPIs, fetchDashboardConfig, fetchCustomWidgets, fetchSupervisados, mesSeleccionado, anioSeleccionado]);

  const saveDashboardConfig = async () => {
    try {
      setSavingConfig(true);
      await axios.put(`${API}/dashboard/config`, dashboardConfig, getAuthHeaders());
      toast.success("Configuración guardada");
      setConfigMode(false);
      setShowConfigDialog(false);
    } catch (err) {
      console.error("Error saving config:", err);
      toast.error("Error al guardar la configuración");
    } finally {
      setSavingConfig(false);
    }
  };

  const resetDashboardConfig = async () => {
    try {
      setSavingConfig(true);
      await axios.post(`${API}/dashboard/config/reset`, {}, getAuthHeaders());
      await fetchDashboardConfig();
      toast.success("Configuración restablecida");
      setShowConfigDialog(false);
    } catch (err) {
      console.error("Error resetting config:", err);
      toast.error("Error al restablecer la configuración");
    } finally {
      setSavingConfig(false);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (active.id !== over?.id) {
      setDashboardConfig((prev) => {
        const oldIndex = prev.widgets.findIndex((w) => w.widget_id === active.id);
        const newIndex = prev.widgets.findIndex((w) => w.widget_id === over.id);
        
        const newWidgets = arrayMove(prev.widgets, oldIndex, newIndex).map((w, i) => ({
          ...w,
          order: i
        }));
        
        return { ...prev, widgets: newWidgets };
      });
    }
  };

  const toggleWidget = (widgetId) => {
    setDashboardConfig((prev) => ({
      ...prev,
      widgets: prev.widgets.map((w) =>
        w.widget_id === widgetId ? { ...w, enabled: !w.enabled } : w
      )
    }));
  };

  const moveWidget = (widgetId, direction) => {
    setDashboardConfig((prev) => {
      const idx = prev.widgets.findIndex((w) => w.widget_id === widgetId);
      if ((direction === -1 && idx === 0) || (direction === 1 && idx === prev.widgets.length - 1)) {
        return prev;
      }
      const newWidgets = [...prev.widgets];
      const temp = newWidgets[idx];
      newWidgets[idx] = newWidgets[idx + direction];
      newWidgets[idx + direction] = temp;
      return {
        ...prev,
        widgets: newWidgets.map((w, i) => ({ ...w, order: i }))
      };
    });
  };

  const kpis = data.kpis || {};
  const userInfo = data.user_info || {};
  const recentActivity = data.recent_activity || [];

  const getAlertCount = () => {
    let count = 0;
    // Only count alerts for modules the user has access to
    if (hasModuleAccess('mesa_ayuda') && kpis.tickets?.urgentes > 0) count += kpis.tickets.urgentes;
    if (hasModuleAccess('gestion_humana') && kpis.gestion_humana?.total_pendientes > 0) count += kpis.gestion_humana.total_pendientes;
    if (hasModuleAccess('mensajeria') && kpis.mensajeria?.pendientes > 0) count += kpis.mensajeria.pendientes;
    if (hasModuleAccess('requisiciones') && kpis.requisiciones?.pendientes > 0) count += kpis.requisiciones.pendientes;
    if (hasModuleAccess('finanzas') && kpis.inventario?.bajo_stock > 0) count += kpis.inventario.bajo_stock;
    if (hasModuleAccess('whatsapp') && kpis.whatsapp?.no_leidos > 0) count += kpis.whatsapp.no_leidos;
    return count;
  };

  const getPendientesChartData = () => {
    const data = [];
    // Only include data for modules the user has access to
    if (hasModuleAccess('mesa_ayuda') && kpis.tickets?.pendientes > 0) data.push({ name: 'Tickets', value: kpis.tickets.pendientes, color: '#f97316' });
    if (hasModuleAccess('mensajeria') && kpis.mensajeria?.pendientes > 0) data.push({ name: 'Mensajería', value: kpis.mensajeria.pendientes, color: '#6366f1' });
    if (hasModuleAccess('gestion_humana') && kpis.gestion_humana?.vacaciones_pendientes > 0) data.push({ name: 'Vacaciones', value: kpis.gestion_humana.vacaciones_pendientes, color: '#ec4899' });
    if (hasModuleAccess('gestion_humana') && kpis.gestion_humana?.permisos_pendientes > 0) data.push({ name: 'Permisos', value: kpis.gestion_humana.permisos_pendientes, color: '#f43f5e' });
    if (hasModuleAccess('requisiciones') && kpis.requisiciones?.pendientes > 0) data.push({ name: 'Requisiciones', value: kpis.requisiciones.pendientes, color: '#a855f7' });
    return data;
  };

  const getResumenMensualData = () => {
    const data = [];
    // Only include data for modules the user has access to
    if (hasModuleAccess('mesa_ayuda')) data.push({ name: 'Tickets', resueltos: kpis.tickets?.resueltos_mes || 0 });
    if (hasModuleAccess('mensajeria')) data.push({ name: 'Mensajería', resueltos: kpis.mensajeria?.completadas_mes || 0 });
    if (hasModuleAccess('gestion_humana')) data.push({ name: 'Vacaciones', resueltos: kpis.gestion_humana?.vacaciones_aprobadas_mes || 0 });
    if (hasModuleAccess('requisiciones')) data.push({ name: 'Requisiciones', resueltos: kpis.requisiciones?.aprobadas_mes || 0 });
    return data;
  };

  // Filter enabled widgets by user permissions
  const enabledWidgets = dashboardConfig.widgets
    .filter((w) => w.enabled && hasModuleAccess(w.widget_id))
    .sort((a, b) => a.order - b.order);

  const renderModuleCard = (moduleId) => {
    const config = MODULE_CONFIG[moduleId];
    const moduleKpis = kpis[moduleId] || {};
    if (!config) return null;
    
    const Icon = config.icon;
    
    return (
      <Card 
        className={`hover:shadow-lg transition-all cursor-pointer group border-slate-200 dark:border-slate-700 dark:bg-slate-800 ${
          configMode ? 'ring-2 ring-blue-400 ring-opacity-50' : ''
        }`}
        onClick={() => !configMode && navigate(getAccessiblePath(moduleId) || config.path)}
        data-testid={`my-kpi-card-${moduleId}`}
      >
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${config.color} flex items-center justify-center`}>
              <Icon className="w-5 h-5 text-white" />
            </div>
            {moduleKpis.supervisados_count > 0 && (
              <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
                <UserCheck className="w-3 h-3 mr-1" />
                +{moduleKpis.supervisados_count} supervisados
              </Badge>
            )}
          </div>
          <CardTitle className="text-lg mt-2 dark:text-white">{config.label}</CardTitle>
        </CardHeader>
        <CardContent>
          {moduleKpis.error ? (
            <p className="text-sm text-red-500">Error al cargar datos</p>
          ) : (
            <div className="space-y-2">
              {moduleId === "tickets" && (
                <>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500 dark:text-slate-400">Mis abiertos</span>
                    <Badge variant={moduleKpis.abiertos > 3 ? "destructive" : "secondary"}>
                      {moduleKpis.abiertos || 0}
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500 dark:text-slate-400">Resueltos (mes)</span>
                    <span className="text-green-600 dark:text-green-400 font-medium">{moduleKpis.resueltos_mes || 0}</span>
                  </div>
                  {moduleKpis.urgentes > 0 && (
                    <Badge variant="destructive" className="mt-2">
                      <AlertTriangle className="w-3 h-3 mr-1" />
                      {moduleKpis.urgentes} urgentes
                    </Badge>
                  )}
                </>
              )}
              
              {moduleId === "mensajeria" && (
                <>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500 dark:text-slate-400">Mis pendientes</span>
                    <Badge variant={moduleKpis.pendientes > 0 ? "warning" : "secondary"} className={moduleKpis.pendientes > 0 ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400" : ""}>
                      {moduleKpis.pendientes || 0}
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500 dark:text-slate-400">En tránsito</span>
                    <span className="text-blue-600 dark:text-blue-400 font-medium">{moduleKpis.en_transito || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500 dark:text-slate-400">Completadas (mes)</span>
                    <span className="text-green-600 dark:text-green-400 font-medium">{moduleKpis.completadas_mes || 0}</span>
                  </div>
                </>
              )}
              
              {moduleId === "gestion_humana" && (
                <>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500 dark:text-slate-400">Vacaciones pend.</span>
                    <Badge variant={moduleKpis.vacaciones_pendientes > 0 ? "warning" : "secondary"} className={moduleKpis.vacaciones_pendientes > 0 ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400" : ""}>
                      {moduleKpis.vacaciones_pendientes || 0}
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500 dark:text-slate-400">Permisos pend.</span>
                    <Badge variant={moduleKpis.permisos_pendientes > 0 ? "warning" : "secondary"} className={moduleKpis.permisos_pendientes > 0 ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400" : ""}>
                      {moduleKpis.permisos_pendientes || 0}
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500 dark:text-slate-400">Aprobadas (mes)</span>
                    <span className="text-green-600 dark:text-green-400 font-medium">{moduleKpis.vacaciones_aprobadas_mes || 0}</span>
                  </div>
                </>
              )}
              
              {moduleId === "requisiciones" && (
                <>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500 dark:text-slate-400">Total</span>
                    <span className="font-bold text-lg dark:text-white">{moduleKpis.total || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500 dark:text-slate-400">Pendientes</span>
                    <Badge variant={moduleKpis.pendientes > 0 ? "destructive" : "secondary"}>
                      {moduleKpis.pendientes || 0}
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500 dark:text-slate-400">Aprobadas (mes)</span>
                    <span className="text-green-600 dark:text-green-400 font-medium">{moduleKpis.aprobadas_mes || 0}</span>
                  </div>
                </>
              )}
              
              {moduleId === "inventario" && (
                <>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500 dark:text-slate-400">Artículos</span>
                    <span className="font-bold text-lg dark:text-white">{moduleKpis.total_articulos || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500 dark:text-slate-400">Valor Total</span>
                    <span className="font-semibold text-blue-600 dark:text-blue-400">{formatCurrency(moduleKpis.valor_inventario)}</span>
                  </div>
                  {moduleKpis.bajo_stock > 0 && (
                    <Badge variant="destructive" className="mt-2">
                      <AlertTriangle className="w-3 h-3 mr-1" />
                      {moduleKpis.bajo_stock} bajo stock
                    </Badge>
                  )}
                </>
              )}
              
              {moduleId === "visitantes" && (
                <>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500 dark:text-slate-400">Hoy</span>
                    <span className="font-bold text-lg dark:text-white">{moduleKpis.hoy || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500 dark:text-slate-400">En edificio</span>
                    <Badge variant="outline" className="bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                      {moduleKpis.en_edificio || 0}
                    </Badge>
                  </div>
                  {moduleKpis.alertas_activas > 0 && (
                    <Badge variant="destructive" className="mt-2">
                      <Clock className="w-3 h-3 mr-1" />
                      {moduleKpis.alertas_activas} tiempo excedido
                    </Badge>
                  )}
                </>
              )}
              
              {moduleId === "whatsapp" && (
                <>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500 dark:text-slate-400">Conversaciones activas</span>
                    <span className="font-bold text-lg dark:text-white">{moduleKpis.activas || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500 dark:text-slate-400">Mensajes hoy</span>
                    <span className="font-medium dark:text-white">{moduleKpis.mensajes_hoy || 0}</span>
                  </div>
                  {moduleKpis.no_leidos > 0 && (
                    <Badge variant="destructive" className="mt-2">
                      <Bell className="w-3 h-3 mr-1" />
                      {moduleKpis.no_leidos} sin leer
                    </Badge>
                  )}
                </>
              )}
              
              {moduleId === "comparaciones" && (
                <>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500 dark:text-slate-400">Total</span>
                    <span className="font-bold text-lg dark:text-white">{moduleKpis.total || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500 dark:text-slate-400">Este mes</span>
                    <span className="text-indigo-600 dark:text-indigo-400 font-medium">{moduleKpis.este_mes || 0}</span>
                  </div>
                </>
              )}
              
              {!configMode && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="w-full mt-2 group-hover:bg-slate-100 dark:group-hover:bg-slate-700"
                >
                  Ver detalles
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderActivityItem = (item) => {
    const statusConfig = STATUS_CONFIG[item.status] || STATUS_CONFIG.pendiente;
    const StatusIcon = statusConfig.icon;
    
    const typeIcons = {
      ticket: Ticket,
      mensajeria: Truck,
      vacaciones: CalendarDays
    };
    const TypeIcon = typeIcons[item.type] || Activity;
    
    return (
      <div 
        key={`${item.type}-${item.id}`}
        className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700/50 cursor-pointer transition-colors"
        onClick={() => item.path && navigate(item.path)}
      >
        <div className={`w-8 h-8 rounded-lg ${MODULE_CONFIG[item.type]?.bgColor || 'bg-slate-100'} flex items-center justify-center`}>
          <TypeIcon className={`w-4 h-4 ${MODULE_CONFIG[item.type]?.textColor || 'text-slate-600'}`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-slate-900 dark:text-white truncate">
            {item.title}
          </p>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            {formatDate(item.date)}
          </p>
        </div>
        <Badge variant="outline" className={`text-xs ${statusConfig.color}`}>
          <StatusIcon className="w-3 h-3 mr-1" />
          {statusConfig.label}
        </Badge>
      </div>
    );
  };

  if (loading && !data.kpis) {
    return (
      <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
        <Sidebar />
        <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      <PublicidadModal />
      
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden" data-testid="my-dashboard-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-cyan-600 to-blue-700 flex items-center justify-center shadow-lg">
              <LayoutDashboard className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-['Manrope']">
                Mi Dashboard
              </h1>
              <div className="text-slate-500 dark:text-slate-400 mt-1 flex items-center flex-wrap gap-2">
                <span>Bienvenido, <span className="font-medium text-slate-700 dark:text-slate-300">{userInfo.name || user?.name}</span></span>
                {userInfo.is_supervisor && (
                  <Badge variant="outline" className="text-purple-600 bg-purple-50 dark:bg-purple-900/30 dark:text-purple-400">
                    <Shield className="w-3 h-3 mr-1" />
                    Supervisor ({userInfo.supervisados_count} supervisados)
                  </Badge>
                )}
                {userInfo.is_admin && (
                  <Badge variant="outline" className="text-blue-600 bg-blue-50 dark:bg-blue-900/30 dark:text-blue-400">
                    <Shield className="w-3 h-3 mr-1" />
                    Administrador
                  </Badge>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {lastUpdate && (
              <span className="text-sm text-slate-500 dark:text-slate-400">
                <Clock className="w-4 h-4 inline mr-1" />
                {lastUpdate.toLocaleTimeString('es-DO', { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
            <Button 
              variant="outline" 
              onClick={() => setShowConfigDialog(true)}
              data-testid="configure-dashboard-btn"
              className="dark:border-slate-600 dark:text-slate-300"
            >
              <Settings2 className="w-4 h-4 mr-2" />
              Personalizar
            </Button>
            <Button 
              variant="outline" 
              onClick={() => fetchMyKPIs(mesSeleccionado, anioSeleccionado)}
              disabled={loading}
              data-testid="refresh-my-kpis-btn"
              className="dark:border-slate-600 dark:text-slate-300"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              Actualizar
            </Button>
          </div>
        </div>

        {/* Config Mode Banner */}
        {configMode && (
          <Card className="mb-6 border-blue-300 bg-blue-50/50 dark:bg-blue-900/20 dark:border-blue-700">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Settings2 className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  <div>
                    <h3 className="font-semibold text-blue-800 dark:text-blue-300">Modo de Configuración</h3>
                    <p className="text-sm text-blue-600 dark:text-blue-400">Arrastra las tarjetas para reordenarlas</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setConfigMode(false)}>
                    <X className="w-4 h-4 mr-1" />
                    Cancelar
                  </Button>
                  <Button size="sm" onClick={saveDashboardConfig} disabled={savingConfig}>
                    {savingConfig ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                    Guardar
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Alert Summary */}
        {getAlertCount() > 0 && !configMode && (
          <Card className="mb-6 border-amber-200 bg-amber-50/50 dark:bg-amber-900/20 dark:border-amber-800">
            <CardContent className="py-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center">
                  <Bell className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-amber-800 dark:text-amber-300">
                    Tienes {getAlertCount()} elementos que requieren atención
                  </h3>
                  <p className="text-sm text-amber-600 dark:text-amber-400">
                    {hasModuleAccess('mesa_ayuda') && kpis.tickets?.urgentes > 0 && `${kpis.tickets.urgentes} tickets urgentes • `}
                    {hasModuleAccess('gestion_humana') && kpis.gestion_humana?.total_pendientes > 0 && `${kpis.gestion_humana.total_pendientes} solicitudes GH pendientes • `}
                    {hasModuleAccess('mensajeria') && kpis.mensajeria?.pendientes > 0 && `${kpis.mensajeria.pendientes} envíos pendientes • `}
                    {hasModuleAccess('requisiciones') && kpis.requisiciones?.pendientes > 0 && `${kpis.requisiciones.pendientes} requisiciones pendientes`}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tarjetas de Tasas de Cambio */}
        {tasas && (
          <div className="flex flex-wrap gap-3 mb-6">
            {/* USD */}
            <div className="flex items-center gap-4 px-4 py-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm">
              <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/40 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-xs text-slate-500 dark:text-slate-400 font-medium mb-1">USD</p>
                <div className="flex items-center gap-4">
                  <div>
                    <p className="text-[10px] text-slate-400 dark:text-slate-500 uppercase">Compra</p>
                    <p className="text-base font-bold text-slate-800 dark:text-slate-100">
                      {tasas.monedas?.USD?.compra?.toFixed(2) || '--'}
                    </p>
                  </div>
                  <div className="w-px h-8 bg-slate-200 dark:bg-slate-600"></div>
                  <div>
                    <p className="text-[10px] text-slate-400 dark:text-slate-500 uppercase">Venta</p>
                    <p className="text-base font-bold text-green-600 dark:text-green-400">
                      {tasas.monedas?.USD?.venta?.toFixed(2) || '--'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* EUR */}
            <div className="flex items-center gap-4 px-4 py-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm">
              <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center">
                <Euro className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-xs text-slate-500 dark:text-slate-400 font-medium mb-1">EUR</p>
                <div className="flex items-center gap-4">
                  <div>
                    <p className="text-[10px] text-slate-400 dark:text-slate-500 uppercase">Compra</p>
                    <p className="text-base font-bold text-slate-800 dark:text-slate-100">
                      {tasas.monedas?.EUR?.compra?.toFixed(2) || '--'}
                    </p>
                  </div>
                  <div className="w-px h-8 bg-slate-200 dark:bg-slate-600"></div>
                  <div>
                    <p className="text-[10px] text-slate-400 dark:text-slate-500 uppercase">Venta</p>
                    <p className="text-base font-bold text-blue-600 dark:text-blue-400">
                      {tasas.monedas?.EUR?.venta?.toFixed(2) || '--'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Actualización */}
            <button 
              onClick={fetchTasas}
              className="flex items-center gap-2 px-3 py-2 text-xs text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
              title="Actualizar tasas"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loadingTasas ? 'animate-spin' : ''}`} />
              {tasas.fecha && (
                <span>Actualizado {new Date(tasas.fecha).toLocaleTimeString('es-DO', { hour: '2-digit', minute: '2-digit' })}</span>
              )}
            </button>
          </div>
        )}

        {/* Accesos a Portales */}
        {accesosPortales.length > 0 && (
          <Card className="mb-6 dark:bg-slate-800 dark:border-slate-700">
            <CardHeader className="py-3 px-4">
              <CardTitle className="text-sm font-medium flex items-center gap-2 dark:text-white">
                <Globe className="w-4 h-4 text-blue-600" />
                Accesos a Portales
              </CardTitle>
            </CardHeader>
            <CardContent className="py-2 px-4">
              <div className="flex flex-wrap gap-3">
                {accesosPortales.map((acceso) => {
                  // Construir URL de imagen correctamente
                  const imagenUrl = acceso.imagen_url 
                    ? (acceso.imagen_url.startsWith('http') ? acceso.imagen_url : `${API}${acceso.imagen_url}`)
                    : null;
                  
                  return (
                    <a
                      key={acceso.id}
                      href={acceso.url.startsWith('http') ? acceso.url : `https://${acceso.url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm transition-all hover:opacity-90 hover:shadow-md hover:scale-[1.02]"
                      style={{ 
                        backgroundColor: acceso.color_fondo, 
                        color: acceso.color_texto 
                      }}
                      data-testid={`acceso-portal-${acceso.id}`}
                    >
                      {imagenUrl && (
                        <img 
                          src={imagenUrl}
                          alt=""
                          className="w-5 h-5 object-contain"
                          style={{ background: 'transparent' }}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      )}
                      {acceso.nombre}
                      <ExternalLink className="w-3.5 h-3.5 opacity-70" />
                    </a>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quick Stats Row - filtered by permissions */}
        {dashboardConfig.show_quick_stats && !configMode && (
          <div className="kpi-carousel grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {hasModuleAccess('mesa_ayuda') && (
              <Card 
                className="bg-gradient-to-br from-orange-500 to-amber-600 text-white border-0 cursor-pointer hover:shadow-xl hover:scale-[1.02] transition-all duration-200"
                onClick={() => navigate('/mesa-ayuda/tickets?estado=abierto')}
                data-testid="quick-stat-tickets-abiertos"
              >
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-orange-100 text-sm">Tickets Abiertos</p>
                      <p className="text-2xl font-bold mt-1">{kpis.tickets?.abiertos || 0}</p>
                    </div>
                    <Ticket className="w-10 h-10 text-orange-200" />
                  </div>
                </CardContent>
              </Card>
            )}
            
            {hasModuleAccess('mensajeria') && (
              <Card 
                className="bg-gradient-to-br from-indigo-500 to-violet-600 text-white border-0 cursor-pointer hover:shadow-xl hover:scale-[1.02] transition-all duration-200"
                onClick={() => navigate('/mensajeria/solicitudes?estado=pendiente')}
                data-testid="quick-stat-envios-pendientes"
              >
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-indigo-100 text-sm">Envíos Pendientes</p>
                      <p className="text-2xl font-bold mt-1">{kpis.mensajeria?.pendientes || 0}</p>
                    </div>
                    <Truck className="w-10 h-10 text-indigo-200" />
                  </div>
                </CardContent>
              </Card>
            )}
            
            {hasModuleAccess('gestion_humana') && (
              <Card 
                className="bg-gradient-to-br from-rose-500 to-pink-600 text-white border-0 cursor-pointer hover:shadow-xl hover:scale-[1.02] transition-all duration-200"
                onClick={() => navigate('/gestion-humana/solicitudes?estado=pendiente')}
                data-testid="quick-stat-solicitudes-gh"
              >
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-rose-100 text-sm">Solicitudes GH</p>
                      <p className="text-2xl font-bold mt-1">{kpis.gestion_humana?.total_pendientes || 0}</p>
                    </div>
                    <UserCog className="w-10 h-10 text-rose-200" />
                  </div>
                </CardContent>
              </Card>
            )}
            
            {hasModuleAccess('requisiciones') && (
              <Card 
                className="bg-gradient-to-br from-purple-500 to-fuchsia-600 text-white border-0 cursor-pointer hover:shadow-xl hover:scale-[1.02] transition-all duration-200"
                onClick={() => navigate('/requisiciones/pendientes')}
                data-testid="quick-stat-requisiciones"
              >
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-purple-100 text-sm">Requisiciones</p>
                      <p className="text-2xl font-bold mt-1">{kpis.requisiciones?.pendientes || 0}</p>
                    </div>
                    <ClipboardList className="w-10 h-10 text-purple-200" />
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Supervisados Section */}
        {isSupervisor && supervisados.length > 0 && (
          <Card className="mb-8 border-purple-200 dark:border-purple-800" data-testid="supervisados-section">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
                    <Users className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-lg dark:text-white">Mis Supervisados</CardTitle>
                    <CardDescription className="dark:text-slate-400">
                      Tienes {supervisados.length} {supervisados.length === 1 ? 'persona' : 'personas'} bajo tu supervisión
                    </CardDescription>
                  </div>
                </div>
                <Badge className="bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
                  {supervisados.length} {supervisados.length === 1 ? 'Supervisado' : 'Supervisados'}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {supervisados.map((supervisado) => (
                  <div 
                    key={supervisado.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border ${
                      supervisado.es_directo 
                        ? 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800' 
                        : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700'
                    }`}
                    data-testid={`supervisado-${supervisado.id}`}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold text-sm ${
                      supervisado.es_directo 
                        ? 'bg-gradient-to-br from-purple-500 to-purple-700' 
                        : 'bg-gradient-to-br from-slate-400 to-slate-600'
                    }`}>
                      {supervisado.name?.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() || '?'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 dark:text-white truncate" title={supervisado.name}>
                        {supervisado.name}
                        {supervisado.is_active === false && (
                          <span className="ml-1.5 text-[10px] font-semibold uppercase tracking-wide bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-400 rounded px-1.5 py-0.5" data-testid={`badge-inactivo-${supervisado.id}`}>Inactivo</span>
                        )}
                        {!supervisado.es_directo && (
                          <span className="ml-1 text-xs text-slate-400">(indirecto)</span>
                        )}
                      </p>
                      <p className="text-sm text-slate-500 dark:text-slate-400 truncate" title={supervisado.cargo || supervisado.role}>
                        {supervisado.cargo || supervisado.role || 'Sin cargo'}
                      </p>
                      {supervisado.departamento_nombre && (
                        <p className="text-xs text-slate-400 dark:text-slate-500 truncate">
                          {supervisado.departamento_nombre}
                        </p>
                      )}
                      {!supervisado.es_directo && supervisado.supervisor_nombre && (
                        <p className="text-xs text-purple-500 dark:text-purple-400 truncate">
                          Vía: {supervisado.supervisor_nombre}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Desglose de actividad por supervisado */}
              {Array.isArray(data.desglose_supervisados) && data.desglose_supervisados.length > 0 && (
                <div className="mt-6" data-testid="desglose-supervisados">
                  <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-2">
                    <BarChart3 className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                    Desglose de actividad{periodoActual ? ` — ${periodoActual.nombre_mes} ${periodoActual.anio}` : ''}
                  </h4>
                  <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
                    <table className="w-full text-sm" data-testid="desglose-supervisados-table">
                      <thead>
                        <tr className="bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                          <th className="text-left px-3 py-2 font-medium">Supervisado</th>
                          <th className="text-center px-2 py-2 font-medium">Tickets abiertos</th>
                          <th className="text-center px-2 py-2 font-medium">Tickets resueltos (mes)</th>
                          <th className="text-center px-2 py-2 font-medium">Mensajería pend.</th>
                          <th className="text-center px-2 py-2 font-medium">Mensajería compl. (mes)</th>
                          <th className="text-center px-2 py-2 font-medium">GH pend.</th>
                          <th className="text-center px-2 py-2 font-medium">Requisiciones pend.</th>
                          <th className="text-center px-2 py-2 font-medium">Comparaciones (mes)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.desglose_supervisados.map((d) => (
                          <tr key={d.id} className="border-t border-slate-100 dark:border-slate-700" data-testid={`desglose-row-${d.id}`}>
                            <td className="px-3 py-2">
                              <p className="font-medium text-slate-900 dark:text-white truncate max-w-[180px]" title={d.name}>
                                {d.name}
                                {d.is_active === false && (
                                  <span className="ml-1.5 text-[10px] font-semibold uppercase tracking-wide bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-400 rounded px-1.5 py-0.5" data-testid={`badge-inactivo-desglose-${d.id}`}>Inactivo</span>
                                )}
                              </p>
                              <p className="text-xs text-slate-500 dark:text-slate-400 truncate max-w-[180px]">
                                {d.cargo}{d.equipo > 0 ? ` · incluye equipo de ${d.equipo}` : ''}
                              </p>
                            </td>
                            <td className="text-center px-2 py-2">
                              <Badge variant={d.tickets_abiertos > 0 ? "destructive" : "secondary"}>{d.tickets_abiertos}</Badge>
                            </td>
                            <td className="text-center px-2 py-2 text-green-600 dark:text-green-400 font-medium">{d.tickets_resueltos_mes}</td>
                            <td className="text-center px-2 py-2">
                              <Badge variant="secondary" className={d.mensajeria_pendientes > 0 ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400" : ""}>{d.mensajeria_pendientes}</Badge>
                            </td>
                            <td className="text-center px-2 py-2 text-green-600 dark:text-green-400 font-medium">{d.mensajeria_completadas_mes}</td>
                            <td className="text-center px-2 py-2">
                              <Badge variant="secondary" className={d.gh_pendientes > 0 ? "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400" : ""}>{d.gh_pendientes}</Badge>
                            </td>
                            <td className="text-center px-2 py-2">
                              <Badge variant="secondary" className={d.requisiciones_pendientes > 0 ? "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400" : ""}>{d.requisiciones_pendientes}</Badge>
                            </td>
                            <td className="text-center px-2 py-2 text-indigo-600 dark:text-indigo-400 font-medium">{d.comparaciones_mes}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Module Cards - 2 columns */}
          <div className="lg:col-span-2">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext 
                items={enabledWidgets.map((w) => w.widget_id)}
                strategy={rectSortingStrategy}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {enabledWidgets.map((widget) => (
                    <SortableWidgetItem 
                      key={widget.widget_id} 
                      id={widget.widget_id}
                      isConfigMode={configMode}
                    >
                      {renderModuleCard(widget.widget_id)}
                    </SortableWidgetItem>
                  ))}
                </div>
              </SortableContext>
            </DndContext>
            
            {enabledWidgets.length === 0 && (
              <Card className="dark:bg-slate-800 dark:border-slate-700 p-8 text-center">
                <EyeOff className="w-12 h-12 mx-auto text-slate-400 mb-4" />
                <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-300">No hay widgets activos</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">Personaliza tu dashboard para ver tus métricas favoritas</p>
                <Button onClick={() => setShowConfigDialog(true)}>
                  <Settings2 className="w-4 h-4 mr-2" />
                  Configurar Dashboard
                </Button>
              </Card>
            )}
            
            {/* Custom Widgets Section */}
            {customWidgets.length > 0 && (
              <div className="mt-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                    <Target className="w-5 h-5" />
                    Mis Widgets Personalizados
                  </h3>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      fetchAvailableFilters();
                      setShowCustomWidgetDialog(true);
                    }}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Nuevo
                  </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {customWidgets.map((widget) => {
                    const widgetData = customWidgetsData[widget.id] || {};
                    const IconComponent = ICON_MAP[widget.icon] || BarChart3;
                    const colorClass = COLOR_MAP[widget.color] || COLOR_MAP.blue;
                    
                    return (
                      <Card 
                        key={widget.id}
                        className="hover:shadow-lg transition-all border-slate-200 dark:border-slate-700 dark:bg-slate-800 relative group"
                        data-testid={`custom-widget-${widget.id}`}
                      >
                        <button
                          onClick={() => deleteCustomWidget(widget.id)}
                          className="absolute top-2 right-2 p-1.5 rounded-lg bg-red-100 text-red-600 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-200"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                        <CardHeader className="pb-2">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${colorClass} flex items-center justify-center`}>
                              <IconComponent className="w-5 h-5 text-white" />
                            </div>
                            <div>
                              <CardTitle className="text-lg dark:text-white">{widget.name}</CardTitle>
                              <p className="text-xs text-slate-500">
                                {availableFilters?.modules?.find(m => m.id === widget.module)?.name || widget.module}
                              </p>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <div className="text-center py-4">
                            <p className={`text-3xl font-bold ${widgetData.is_currency ? 'text-green-600 dark:text-green-400' : 'text-slate-900 dark:text-white'}`}>
                              {widgetData.is_currency 
                                ? formatCurrency(widgetData.value || 0)
                                : (widgetData.value || 0).toLocaleString()
                              }
                            </p>
                            <p className="text-sm text-slate-500 mt-1">{widgetData.label}</p>
                          </div>
                          {Object.keys(widget.filters || {}).length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {Object.entries(widget.filters).map(([key, value]) => (
                                value && (
                                  <Badge key={key} variant="outline" className="text-xs">
                                    {key}: {value}
                                  </Badge>
                                )
                              ))}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            )}
            
            {/* Add Custom Widget Button (when no custom widgets exist) */}
            {customWidgets.length === 0 && enabledWidgets.length > 0 && (
              <div className="mt-6">
                <Card 
                  className="border-dashed border-2 hover:border-blue-400 cursor-pointer transition-all dark:border-slate-600 dark:hover:border-blue-400"
                  onClick={() => {
                    fetchAvailableFilters();
                    setShowCustomWidgetDialog(true);
                  }}
                >
                  <CardContent className="py-8 text-center">
                    <Plus className="w-10 h-10 mx-auto text-slate-400 mb-2" />
                    <p className="font-medium text-slate-600 dark:text-slate-300">Crear Widget Personalizado</p>
                    <p className="text-sm text-slate-500">Define métricas específicas con filtros personalizados</p>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
          
          {/* Right Column - Charts and Activity */}
          <div className="space-y-6">
            {/* Pie Chart - Pendientes */}
            {dashboardConfig.show_charts && getPendientesChartData().length > 0 && (
              <Card className="dark:bg-slate-800 dark:border-slate-700">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-medium dark:text-white">
                    Distribución de Pendientes
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie
                        data={getPendientesChartData()}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={70}
                        paddingAngle={5}
                        dataKey="value"
                      >
                        {getPendientesChartData().map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}
            
            {/* Bar Chart - Monthly Summary */}
            {dashboardConfig.show_charts && (
              <Card className="dark:bg-slate-800 dark:border-slate-700">
                <CardHeader className="pb-2">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <div>
                      <CardTitle className="text-base font-medium dark:text-white flex items-center gap-2">
                        <CalendarDays className="w-4 h-4" />
                        Resumen del Mes
                        {periodoActual && (
                          <Badge variant="outline" className="ml-2 text-xs">
                            {periodoActual.nombre_mes} {periodoActual.anio}
                          </Badge>
                        )}
                      </CardTitle>
                      <CardDescription className="dark:text-slate-400">
                        Solicitudes completadas/aprobadas
                      </CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <Select 
                        value={mesSeleccionado.toString()} 
                        onValueChange={(v) => setMesSeleccionado(parseInt(v))}
                      >
                        <SelectTrigger className="w-28 h-8 text-xs" data-testid="filtro-mes-trigger">
                          <SelectValue placeholder="Mes" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">Enero</SelectItem>
                          <SelectItem value="2">Febrero</SelectItem>
                          <SelectItem value="3">Marzo</SelectItem>
                          <SelectItem value="4">Abril</SelectItem>
                          <SelectItem value="5">Mayo</SelectItem>
                          <SelectItem value="6">Junio</SelectItem>
                          <SelectItem value="7">Julio</SelectItem>
                          <SelectItem value="8">Agosto</SelectItem>
                          <SelectItem value="9">Septiembre</SelectItem>
                          <SelectItem value="10">Octubre</SelectItem>
                          <SelectItem value="11">Noviembre</SelectItem>
                          <SelectItem value="12">Diciembre</SelectItem>
                        </SelectContent>
                      </Select>
                      <Select 
                        value={anioSeleccionado.toString()} 
                        onValueChange={(v) => setAnioSeleccionado(parseInt(v))}
                      >
                        <SelectTrigger className="w-20 h-8 text-xs" data-testid="filtro-anio-trigger">
                          <SelectValue placeholder="Año" />
                        </SelectTrigger>
                        <SelectContent>
                          {[2024, 2025, 2026, 2027].map(year => (
                            <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={180}>
                    <BarChart data={getResumenMensualData()} layout="vertical">
                      <XAxis type="number" hide />
                      <YAxis dataKey="name" type="category" width={80} tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Bar dataKey="resueltos" fill="#10b981" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}
            
            {/* Recent Activity */}
            {dashboardConfig.show_activity && (
              <Card className="dark:bg-slate-800 dark:border-slate-700">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-medium dark:text-white flex items-center gap-2">
                    <Activity className="w-4 h-4" />
                    Actividad Reciente
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {recentActivity.length === 0 ? (
                    <div className="text-center py-6 text-slate-500 dark:text-slate-400">
                      <Activity className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Sin actividad reciente</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-slate-100 dark:divide-slate-700">
                      {recentActivity.slice(0, 6).map(renderActivityItem)}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Configuration Dialog */}
        <Dialog open={showConfigDialog} onOpenChange={setShowConfigDialog}>
          <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
            <DialogHeader className="flex-shrink-0">
              <DialogTitle className="flex items-center gap-2">
                <Settings2 className="w-5 h-5" />
                Personalizar Mi Dashboard
              </DialogTitle>
              <DialogDescription>
                Selecciona qué widgets mostrar y reorganízalos según tu preferencia
              </DialogDescription>
            </DialogHeader>
            
            <div className="flex-1 overflow-y-auto pr-2" style={{ maxHeight: 'calc(85vh - 180px)' }}>
              <div className="space-y-6 py-4">
                {/* General Options */}
                <div className="space-y-4">
                  <h4 className="font-medium text-sm text-slate-900 dark:text-white">Opciones Generales</h4>
                  
                  <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                    <div className="flex items-center gap-3">
                      <BarChart3 className="w-5 h-5 text-slate-500" />
                      <div>
                        <Label>Mostrar Gráficos</Label>
                        <p className="text-xs text-slate-500">Gráficos de distribución y resumen mensual</p>
                      </div>
                    </div>
                    <Switch
                      checked={dashboardConfig.show_charts}
                      onCheckedChange={(checked) => setDashboardConfig(prev => ({ ...prev, show_charts: checked }))}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Activity className="w-5 h-5 text-slate-500" />
                      <div>
                        <Label>Mostrar Actividad Reciente</Label>
                        <p className="text-xs text-slate-500">Timeline de tus últimas acciones</p>
                      </div>
                    </div>
                    <Switch
                      checked={dashboardConfig.show_activity}
                      onCheckedChange={(checked) => setDashboardConfig(prev => ({ ...prev, show_activity: checked }))}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                    <div className="flex items-center gap-3">
                      <TrendingUp className="w-5 h-5 text-slate-500" />
                      <div>
                        <Label>Mostrar Estadísticas Rápidas</Label>
                        <p className="text-xs text-slate-500">Tarjetas con totales en la parte superior</p>
                      </div>
                    </div>
                    <Switch
                      checked={dashboardConfig.show_quick_stats}
                      onCheckedChange={(checked) => setDashboardConfig(prev => ({ ...prev, show_quick_stats: checked }))}
                    />
                  </div>
                </div>
                
                {/* Widgets */}
                <div className="space-y-4">
                  <h4 className="font-medium text-sm text-slate-900 dark:text-white">Widgets de Módulos</h4>
                  <p className="text-xs text-slate-500">Activa/desactiva widgets y usa las flechas para reordenarlos</p>
                  
                  <div className="space-y-2">
                    {dashboardConfig.widgets.map((widget, index) => {
                      const config = MODULE_CONFIG[widget.widget_id];
                      if (!config) return null;
                      const Icon = config.icon;
                      
                      return (
                        <div 
                          key={widget.widget_id}
                          className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${
                            widget.enabled 
                              ? 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700' 
                              : 'bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 opacity-60'
                          }`}
                        >
                          <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${config.color} flex items-center justify-center flex-shrink-0`}>
                            <Icon className="w-5 h-5 text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm text-slate-900 dark:text-white">{config.label}</p>
                            <p className="text-xs text-slate-500 truncate">{config.description}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="flex flex-col gap-0.5">
                              <button
                                onClick={() => moveWidget(widget.widget_id, -1)}
                                disabled={index === 0}
                                className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-30 disabled:cursor-not-allowed"
                              >
                                <ChevronUp className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => moveWidget(widget.widget_id, 1)}
                                disabled={index === dashboardConfig.widgets.length - 1}
                                className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-30 disabled:cursor-not-allowed"
                              >
                                <ChevronDown className="w-4 h-4" />
                              </button>
                            </div>
                            <Switch
                              checked={widget.enabled}
                              onCheckedChange={() => toggleWidget(widget.widget_id)}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
            
            <DialogFooter className="flex justify-between border-t pt-4 flex-shrink-0">
              <Button 
                variant="outline" 
                onClick={resetDashboardConfig}
                disabled={savingConfig}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Restablecer
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowConfigDialog(false)}>
                  Cancelar
                </Button>
                <Button onClick={saveDashboardConfig} disabled={savingConfig}>
                  {savingConfig ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Guardar Cambios
                </Button>
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Custom Widget Creation Dialog */}
        <Dialog open={showCustomWidgetDialog} onOpenChange={setShowCustomWidgetDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Target className="w-5 h-5" />
                Crear Widget Personalizado
              </DialogTitle>
              <DialogDescription>
                Define métricas específicas con filtros para tu dashboard
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Widget Name */}
              <div className="space-y-2">
                <Label htmlFor="widget-name">Nombre del Widget *</Label>
                <Input
                  id="widget-name"
                  placeholder="Ej: Tickets Urgentes TI"
                  value={newCustomWidget.name}
                  onChange={(e) => setNewCustomWidget(prev => ({ ...prev, name: e.target.value }))}
                />
              </div>
              
              {/* Module Selection */}
              <div className="space-y-2">
                <Label>Módulo *</Label>
                <Select
                  value={newCustomWidget.module}
                  onValueChange={(value) => setNewCustomWidget(prev => ({ 
                    ...prev, 
                    module: value,
                    metric: "count",
                    filters: {}
                  }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar módulo..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availableFilters?.modules?.map((mod) => (
                      <SelectItem key={mod.id} value={mod.id}>
                        {mod.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Metric Selection */}
              {newCustomWidget.module && (
                <div className="space-y-2">
                  <Label>Métrica</Label>
                  <Select
                    value={newCustomWidget.metric}
                    onValueChange={(value) => setNewCustomWidget(prev => ({ ...prev, metric: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar métrica..." />
                    </SelectTrigger>
                    <SelectContent>
                      {availableFilters?.modules
                        ?.find(m => m.id === newCustomWidget.module)
                        ?.metrics?.map((metric) => (
                          <SelectItem key={metric.id} value={metric.id}>
                            {metric.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {/* Dynamic Filters */}
              {newCustomWidget.module && (
                <div className="space-y-3">
                  <Label>Filtros</Label>
                  {availableFilters?.modules
                    ?.find(m => m.id === newCustomWidget.module)
                    ?.filters?.map((filter) => (
                      <div key={filter.id} className="space-y-1">
                        <Label className="text-xs text-slate-500">{filter.name}</Label>
                        <Select
                          value={newCustomWidget.filters[filter.id] || ""}
                          onValueChange={(value) => setNewCustomWidget(prev => ({
                            ...prev,
                            filters: { ...prev.filters, [filter.id]: value === "_none_" ? "" : value }
                          }))}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder={`Todos (sin filtro)`} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="_none_">Todos (sin filtro)</SelectItem>
                            {filter.options?.map((opt) => (
                              <SelectItem key={opt.value} value={String(opt.value)}>
                                {opt.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    ))}
                </div>
              )}
              
              {/* Color and Icon */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Color</Label>
                  <Select
                    value={newCustomWidget.color}
                    onValueChange={(value) => setNewCustomWidget(prev => ({ ...prev, color: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {availableFilters?.colors?.map((color) => (
                        <SelectItem key={color.id} value={color.id}>
                          <div className="flex items-center gap-2">
                            <div className={`w-4 h-4 rounded bg-gradient-to-br ${color.class}`} />
                            {color.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Icono</Label>
                  <Select
                    value={newCustomWidget.icon}
                    onValueChange={(value) => setNewCustomWidget(prev => ({ ...prev, icon: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {availableFilters?.icons?.map((icon) => {
                        const IconComp = ICON_MAP[icon.id] || BarChart3;
                        return (
                          <SelectItem key={icon.id} value={icon.id}>
                            <div className="flex items-center gap-2">
                              <IconComp className="w-4 h-4" />
                              {icon.name}
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Preview */}
              {newCustomWidget.name && newCustomWidget.module && (
                <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <p className="text-xs text-slate-500 mb-2">Vista previa:</p>
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${COLOR_MAP[newCustomWidget.color]} flex items-center justify-center`}>
                      {(() => {
                        const IconComp = ICON_MAP[newCustomWidget.icon] || BarChart3;
                        return <IconComp className="w-5 h-5 text-white" />;
                      })()}
                    </div>
                    <div>
                      <p className="font-medium text-sm">{newCustomWidget.name}</p>
                      <p className="text-xs text-slate-500">
                        {availableFilters?.modules?.find(m => m.id === newCustomWidget.module)?.name}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCustomWidgetDialog(false)}>
                Cancelar
              </Button>
              <Button onClick={createCustomWidget} disabled={creatingWidget || !newCustomWidget.name || !newCustomWidget.module}>
                {creatingWidget ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                Crear Widget
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
